'use client';
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { z } from 'zod';
import { useConfig, useMe } from '../../lib/queries';
import { holdsAnswer } from '../../lib/saveFeedback';
import { elowenClient, ElowenApiError } from '../../lib/elowenClient';
import { subscribeVoiceRingEvents } from '../../lib/voiceEvents';
import type { ProjectExecutionRef } from '../../lib/types';
import { useBrainChat } from '../advisor/BrainChatProvider';
import { useVoiceCall, type VoiceCallSnapshot } from './useVoiceCall';
import { IncomingCallPrompt, type IncomingRing } from './IncomingCallPrompt';

const isStaleRingError = (error: unknown): boolean => error instanceof ElowenApiError && [404, 409, 410].includes(error.status);

const ringSchema = z.object({
  ringId: z.string().min(1), kind: z.enum(['question', 'done']), sessionId: z.string().min(1), title: z.string(),
  expiresAt: z.iso.datetime({ offset: true }),
});
const endSchema = z.object({ ringId: z.string(), reason: z.enum(['timeout', 'declined', 'accepted', 'settled', 'restart', 'unavailable']) });
interface VoiceContext { available: boolean; active: boolean; snapshot: VoiceCallSnapshot; start(): void; startNew(target?: ProjectExecutionRef): void; hangup(): void; dismiss(): void }
const context = createContext<VoiceContext | null>(null);
export const useVoiceCallController = () => useContext(context);

export function VoiceCallProvider({ children }: { children: ReactNode }) {
  const chat = useBrainChat();
  const router = useRouter();
  const { data: config } = useConfig();
  const { data: me } = useMe();
  const available = !!config?.voice?.enabled && !!me?.user.can_use_voice;
  const chatRef = useRef(chat); chatRef.current = chat;
  const { session, snapshot } = useVoiceCall();
  useEffect(() => {
    // An ordinary end needs no notice: the hang-up button leaving is the signal. Errors stay until dismissed.
    if (snapshot.phase === 'ended') session.dismiss();
  }, [snapshot.phase, session]);
  const [incoming, setIncoming] = useState<IncomingRing | null>(null);
  const incomingRef = useRef(incoming); incomingRef.current = incoming;
  const lookup = useRef(0);
  const availableRef = useRef(available); availableRef.current = available;
  const validate = useCallback(async (ringId: string, strict = false) => {
    const version = ++lookup.current;
    setIncoming({ ringId, status: 'loading' });
    try {
      const ring = ringSchema.parse(await elowenClient.voiceRing(ringId));
      if (version !== lookup.current) return;
      const expired = Date.parse(ring.expiresAt) <= Date.now();
      setIncoming({ ringId, ring, status: expired ? 'stale' : 'ready', ...(expired ? { reason: 'timeout' as const } : {}) });
    } catch (error) {
      if (version !== lookup.current) return;
      const stale = isStaleRingError(error);
      setIncoming({ ringId, status: stale ? 'stale' : 'error' });
      if (strict && !stale) throw error;
    }
  }, []);
  const pathname = usePathname();
  const ringId = useSearchParams().get('voiceRing');
  useEffect(() => {
    if (pathname === '/chat' && ringId) void validate(ringId);
  }, [pathname, ringId, validate]);
  useEffect(() => {
    if (!available) return;
    const unsubscribe = subscribeVoiceRingEvents(event => {
      if (event.type === 'voice_ring') {
        const result = ringSchema.safeParse(event.data);
        if (result.success) void validate(result.data.ringId);
        else setIncoming({ ringId: '', status: 'error' });
        return;
      }
      const result = endSchema.safeParse(event.data);
      if (!result.success || incomingRef.current?.ringId !== result.data.ringId) return;
      ++lookup.current;
      setIncoming(current => current ? { ...current, status: 'stale', reason: result.data.reason } : null);
    });
    const lookups = lookup;
    return () => { unsubscribe(); ++lookups.current; };
  }, [available, validate]);
  useEffect(() => {
    if (incoming?.status !== 'ready' || !incoming.ring) return;
    const timer = setTimeout(() => setIncoming(current => current?.ringId === incoming.ringId
      ? { ...current, status: 'stale', reason: 'timeout' } : current), Math.max(0, Date.parse(incoming.ring.expiresAt) - Date.now()));
    return () => clearTimeout(timer);
  }, [incoming]);
  const start = () => {
    if (availableRef.current && chatRef.current.pluginSessionId) {
      void session.start(chatRef.current.pluginSessionId);
    }
  };
  const startNew = (target?: ProjectExecutionRef) => {
    if (!availableRef.current) return;
    // Capture starts synchronously; the existing chat action creates the conversation in the chosen
    // project before the call is opened against it.
    void session.start(async signal => {
      const sessionId = await chatRef.current.startNewConversation({ target });
      if (!signal.aborted) router.push('/chat');
      return sessionId;
    });
  };
  const accept = () => {
    const current = incomingRef.current;
    if (!availableRef.current || current?.status !== 'ready' || !current.ring
      || Date.parse(current.ring.expiresAt) <= Date.now() || snapshot.phase === 'active') return;
    ++lookup.current; setIncoming(null);
    const sessionId = current.ring.sessionId;
    // Capture still starts in this click; a failed navigation uses the ordinary call error state.
    void session.start(chatRef.current.pluginSessionId === sessionId ? sessionId : async () => {
      await chatRef.current.switchSession({ session: sessionId });
      return sessionId;
    }, current.ringId);
  };
  const decline = async () => {
    const current = incomingRef.current;
    if (current?.status !== 'ready' || (current.declineFailure && holdsAnswer(current.declineFailure.error))) return;
    setIncoming({ ...current, status: 'declining', declineFailure: undefined });
    try {
      await elowenClient.declineVoiceRing(current.ringId);
      setIncoming(value => value?.ringId === current.ringId ? { ...value, status: 'stale', reason: 'declined' } : value);
    } catch (error) {
      const stale = isStaleRingError(error);
      setIncoming(value => value?.ringId === current.ringId && value.status === 'declining'
        ? { ...value, status: stale ? 'stale' : 'ready', ...(!stale ? { declineFailure: { error } } : {}) } : value);
    }
  };
  const close = () => { ++lookup.current; setIncoming(null); };
  return <context.Provider value={{ available, active: snapshot.phase === 'active', snapshot, start, startNew, hangup: session.hangup, dismiss: session.dismiss }}>
    {children}
    {incoming && snapshot.phase !== 'active' && <IncomingCallPrompt incoming={incoming} onAccept={accept}
      onDecline={() => void decline()} onRetry={() => void validate(incoming.ringId)} onVerify={() => validate(incoming.ringId, true)} onClose={close} available={available} />}
  </context.Provider>;
}
